import {
  Component, HostBinding, Input, OnInit, Optional, Self, Output, EventEmitter,
  HostListener, ElementRef, ViewChild, ChangeDetectorRef
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NgControl } from '@angular/forms';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { CommonModule, NgFor, NgIf } from '@angular/common';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { Moment } from 'moment';

type InputType =
  | 'text'
  | 'password'
  | 'number'
  | 'email'
  | 'url'
  | 'textarea'
  | 'select'
  | 'datepicker';
export interface SelectOption {
  label: string;
  value: string | number | boolean;
  icon?: string;
  disabled?: boolean;
}

@Component({
  selector: 'ui-form-field',
  standalone: true,
  imports: [NgIf, NgFor, CommonModule, SvgIconComponent, MatDatepickerModule, FormsModule],
  templateUrl: './form-field.component.html',
  styleUrls: ['./form-field.component.scss'],
})
export class FormFieldComponent implements OnInit, ControlValueAccessor {
  /** Forwarded onto the native input's `id` attribute (e.g. so a global
   * recovery handler like `index.html`'s `gm_authFailure` can target it via
   * `document.getElementById`) -- optional, unset by default. */
  @Input() id = '';
  @Input() label = '';
  @Input() placeholder = '';
  @Input() type: InputType = 'text';
  @Input() suffixIcon = '';
  @Input() prefixIcon = '';
  @Input() suffixIconSize: 'small' | 'medium' | 'large' = 'medium';
  /** Renders the suffix icon as a real button and emits `suffixIconClick`.
   * Off by default so existing decorative suffixes stay inert. */
  @Input() suffixIconClickable = false;
  @Input() suffixIconAriaLabel = '';
  /** Forwarded to `aria-pressed` — set it only for toggle-style suffixes. */
  @Input() suffixIconPressed: boolean | null = null;
  @Input() suffixText = '';
  @Input() numberAlignment: 'left' | 'right' = 'left';
  @Input() isOptional = false;
  @Input() errorMessages: Record<string, string> = {};
  @Input() fullWidth = false;
  @Input() min: number | null = null;
  @Input() max: number | null = null;
  @Input() step: number | null = null;
  @Input() options: SelectOption[] = [];
  @Input() multiple = false;
  /** Multi-select dropdown only: shows a search input at the top of the
   * open dropdown panel that filters `options` by label as you type. */
  @Input() searchable = false;
  @Input() searchPlaceholder = 'Search...';
  @Input() minDate: Moment | null = null;
  @Input() maxDate: Moment | null = null;
  @Input() hideErrors = false;
  @Input() emptySelectText = 'Select options...';
  @Input() autocomplete: string = '';
  /** Forwarded onto the native control as `inputmode`. Without
   *  `inputmode="numeric"` a mobile keyboard opens on letters for a field that
   *  only ever takes digits. */
  @Input() inputMode: string = '';
  /** Forwarded onto the native control as `maxlength` -- a hard stop the
   *  browser enforces, distinct from a Validators.maxLength message. */
  @Input() maxLength: number | null = null;
  /** Forwarded onto the native control as `data-testid` -- distinct from `id`,
   * which exists so a global handler can reach the field by `getElementById`. */
  @Input() testId?: string;

  @Output() focus = new EventEmitter<void>();
  @Output() blur = new EventEmitter<void>();
  @Output() suffixIconClick = new EventEmitter<void>();

  value: any = '';
  isDisabled = false;
  touched = false;
  control: any;
  isDropdownOpen = false;
  dropdownPosition = { top: 0, left: 0, width: 0 };
  searchTerm = '';
  /** `options` narrowed by `searchTerm` -- recomputed only in `onSearchInput`,
   * NOT a method called from the template's `*ngFor`. `.filter()` returns a
   * new array every call, and Angular re-evaluates template method calls on
   * every change-detection cycle, so binding `*ngFor` directly to a
   * filtering method tears down and rebuilds the whole option list on every
   * CD tick (mouse moves, focus events, anything) instead of just on input. */
  filteredOptions: SelectOption[] = [];

  @ViewChild('inputElement') inputElement?: ElementRef<HTMLInputElement>;

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    private cdr: ChangeDetectorRef,
    private hostElement: ElementRef<HTMLElement>,
  ) {
    if (ngControl) {
      ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    this.control = this.ngControl?.control;
    if (this.multiple && this.value === '') {
      this.value = [];
    }
    this.filteredOptions = this.options;
  }

  @HostBinding('class.full-width') get isFullWidth() {
    return this.fullWidth;
  }

  // ---- ControlValueAccessor API ----
  onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  writeValue(value: any): void {
    this.value = value;
    // update DOM input directly if present
    if (this.inputElement) {
      const nativeInput = this.inputElement.nativeElement;
      nativeInput.value = value == null ? '' : value;
    }
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.cdr.markForCheck();
  }
  // ----------------------------------

  onNativeInput(event: Event): void {
    const el = event.target as HTMLInputElement;
    const raw = el.value;
    const parsed =
      this.type === 'number' ? (raw === '' ? null : Number(raw)) : raw;
    this.value = parsed;
    this.onChange(parsed);
  }

  onDateChange(event: any): void {
    const date = event.value;
    this.value = date ? date.format('YYYY-MM-DD') : '';
    this.onChange(this.value);
  }

  clearDatePicker(event: MouseEvent) {
    event.stopPropagation();
    this.value = null;
    this.onChange(this.value);
  }

  onFocus(): void {
    this.focus.emit();
  }

  onBlur(): void {
    this.onTouched();
    this.touched = true;
    this.blur.emit();
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') event.preventDefault();
  }

  // ---- Validation helpers ----
  get showError(): boolean {
    const ctrl = this.control;
    return !!ctrl && ctrl.invalid && (ctrl.dirty || ctrl.touched);
  }

  get errors(): string[] {
    const errors = this.control?.errors ?? {};
    return Object.keys(errors).map((key) => this.errorMessages[key] || key);
  }

  // ---- Dropdown & multiple select logic ----
  toggleDropdown(event?: MouseEvent): void {
    if (!this.isDisabled) {
      this.isDropdownOpen = !this.isDropdownOpen;
      if (this.isDropdownOpen) {
        this.calculateDropdownPosition(event?.currentTarget as HTMLElement | null);
      }
    }
  }

  private calculateDropdownPosition(triggerElement?: HTMLElement | null): void {
    setTimeout(() => {
      const trigger = triggerElement
        || this.hostElement.nativeElement.querySelector('.multi-select-trigger, .single-select-trigger') as HTMLElement;
      if (trigger) {
        const rect = trigger.getBoundingClientRect();
        const containerRect = this.getFixedPositionContainerRect();
        const containerWidth = containerRect.right - containerRect.left;
        const containerHeight = containerRect.bottom - containerRect.top;
        const spacing = 8;
        const dropdownMaxHeight = 200;
        const estimatedOptionHeight = 44;
        const estimatedDropdownHeight = Math.min(
          dropdownMaxHeight,
          Math.max(estimatedOptionHeight, this.options.length * estimatedOptionHeight),
        );
        const dropdownWidth = Math.max(rect.width, 200);

        const fitsBelow = rect.bottom + spacing + estimatedDropdownHeight <= containerRect.bottom;
        const preferredTop = fitsBelow
          ? rect.bottom + 4
          : Math.max(containerRect.top + spacing, rect.top - estimatedDropdownHeight - 4);
        const maxLeft = Math.max(
          containerRect.left + spacing,
          containerRect.left + containerWidth - dropdownWidth - spacing,
        );
        const preferredLeft = Math.min(
          Math.max(containerRect.left + spacing, rect.left),
          maxLeft,
        );
        const relativeTop = preferredTop - containerRect.top;
        const relativeLeft = preferredLeft - containerRect.left;

        this.dropdownPosition = {
          top: Math.max(spacing, Math.min(relativeTop, containerHeight - spacing - estimatedDropdownHeight)),
          left: Math.max(spacing, relativeLeft),
          width: dropdownWidth,
        };
      }
    });
  }

  private getFixedPositionContainerRect(): DOMRect {
    const host = this.hostElement.nativeElement;
    let ancestor = host.parentElement;

    while (ancestor) {
      const styles = window.getComputedStyle(ancestor);
      const hasCustomContainingBlock =
        styles.transform !== 'none'
        || styles.perspective !== 'none'
        || styles.filter !== 'none'
        || styles.contain.includes('paint');

      if (hasCustomContainingBlock) {
        return ancestor.getBoundingClientRect();
      }

      ancestor = ancestor.parentElement;
    }

    return new DOMRect(0, 0, window.innerWidth, window.innerHeight);
  }

  closeDropdown(): void {
    this.isDropdownOpen = false;
    if (this.searchable) {
      this.searchTerm = '';
      this.filteredOptions = this.options;
    }
  }

  /** Called on input, not from the template -- see `filteredOptions` for why
   * that distinction matters. Already-selected options stay visible even if
   * they no longer match the current term, so typing doesn't hide a pick. */
  onSearchInput(term: string): void {
    this.searchTerm = term;
    const normalized = term.trim().toLowerCase();
    this.filteredOptions = normalized
      ? this.options.filter((opt) => this.isOptionSelected(opt.value) || opt.label.toLowerCase().includes(normalized))
      : this.options;
  }

  isOptionSelected(optionValue: any): boolean {
    return Array.isArray(this.value) && this.value.includes(optionValue);
  }

  toggleOption(optionValue: any, optionDisabled = false): void {
    if (!this.multiple || optionDisabled) return;
    const currentValue = Array.isArray(this.value) ? [...this.value] : [];
    const exists = currentValue.includes(optionValue);
    this.value = exists
      ? currentValue.filter((v) => v !== optionValue)
      : [...currentValue, optionValue];
    this.onChange(this.value);
  }

  getDisplayText(): string {
    if (!this.multiple || !Array.isArray(this.value) || !this.value.length) {
      return this.placeholder || this.emptySelectText;
    }
    if (this.value.length === 1) {
      const selected = this.options.find((o) => o.value === this.value[0]);
      return selected ? selected.label : '';
    }
    return `${this.value.length} options selected`;
  }

  getSingleSelectDisplayText(): string {
    const selected = this.options.find((option) => option.value === this.value);
    if (selected) {
      return selected.label;
    }
    return this.placeholder || this.emptySelectText;
  }

  get hasOptionIcons(): boolean {
    return this.options.some((option) => !!option.icon);
  }

  selectSingleOption(option: SelectOption): void {
    if (option.disabled) {
      return;
    }
    this.value = option.value;
    this.onChange(option.value);
    this.onTouched();
    this.closeDropdown();
  }

  trackByValue(_: number, option: SelectOption) {
    return option.value;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.custom-multi-select') && !target.closest('.custom-single-select') && this.isDropdownOpen) {
      this.closeDropdown();
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.isDropdownOpen) {
      this.calculateDropdownPosition();
    }
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (this.isDropdownOpen) {
      this.calculateDropdownPosition();
    }
  }
}
